// PDFUTILS.ts
// Requires pdfjs-dist to be available as window.pdfjsLib (as you already assumed)

declare global {
  interface Window {
    pdfjsLib: any;
  }
}

/**
 * Counts the number of pages in a PDF file.
 * @param file The PDF File object.
 * @returns A promise that resolves to the number of pages.
 */
export async function countPdfPages(file: File): Promise<number> {
    if (!window.pdfjsLib) {
        console.error('pdfjsLib is not loaded.');
        return 1; // Fallback for safety
    }
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${window.pdfjsLib.version}/pdf.worker.min.js`;

    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = window.pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    return pdf.numPages;
}

type TextItem = {
  str: string;
  transform: number[]; // [a,b,c,d,e,f] where e=x, f=y in PDF space (scaled)
  width?: number;
  height?: number;
  fontName?: string;
};

function isLikelyFooterOrHeader(line: string) {
  const t = line.trim();
  if (!t) return true;

  // remove common footer/header noise
  if (/^page\s+\d+(\s+of\s+\d+)?$/i.test(t)) return true;
  if (/^©\s*\d{4}\s*one\s*size\s*fits\s*none/i.test(t)) return true;
  if (/^a-typical/i.test(t) && /diagnostic/i.test(t) && t.length < 60) return true;

  return false;
}

function normalizeWhitespace(s: string) {
  return s.replace(/\s+/g, ' ').trim();
}

/**
 * Reconstructs lines from PDF.js textContent using Y (line) clustering and X sorting.
 * This preserves line breaks, improves paragraph structure, and keeps bullets readable.
 */
function reconstructPageText(items: TextItem[]) {
  // Filter out empty strings early
  const filtered = items
    .map(it => ({ ...it, str: (it.str ?? '').replace(/\u00A0/g, ' ') }))
    .filter(it => it.str && it.str.trim().length > 0);

  if (filtered.length === 0) return '';

  // Group by Y with tolerance (PDF coordinate jitter)
  // y is transform[5]
  const Y_TOL = 2.5;

  type LineBucket = { y: number; items: { x: number; str: string }[] };
  const buckets: LineBucket[] = [];

  for (const it of filtered) {
    const x = it.transform?.[4] ?? 0;
    const y = it.transform?.[5] ?? 0;

    // find existing bucket within tolerance
    let bucket = buckets.find(b => Math.abs(b.y - y) <= Y_TOL);
    if (!bucket) {
      bucket = { y, items: [] };
      buckets.push(bucket);
    }
    bucket.items.push({ x, str: it.str });
  }

  // Sort buckets top to bottom: higher y is higher on page in PDF.js coordinates
  buckets.sort((a, b) => b.y - a.y);

  // For each line, sort left to right and stitch with spacing heuristics
  const lines: string[] = [];

  for (const b of buckets) {
    b.items.sort((a, c) => a.x - c.x);

    // Basic stitching: insert a space if previous token doesn't end with hyphen
    // and current token doesn't start with punctuation.
    let line = '';
    for (const tok of b.items) {
      const t = tok.str;
      if (!t) continue;

      if (!line) {
        line = t;
        continue;
      }

      const last = line.slice(-1);
      const startsPunct = /^[,.;:)\]]/.test(t);
      const lastIsHyphen = last === '-' || last === '–' || last === '—';

      if (lastIsHyphen) {
        // join word-break hyphenations
        line = line.slice(0, -1) + t;
      } else if (startsPunct) {
        line = line + t;
      } else {
        line = line + ' ' + t;
      }
    }

    line = normalizeWhitespace(line);

    // Drop obvious header/footer lines
    if (!isLikelyFooterOrHeader(line)) {
      lines.push(line);
    }
  }

  // Paragraph inference:
  // Insert blank line after lines that look like section headings or end with a period and are long.
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const cur = lines[i];
    const next = lines[i + 1] ?? '';

    out.push(cur);

    const isHeading =
      (/^(?:\d+\.\s+|SECTION\s+\d+\s*:)/i.test(cur) && cur.length < 140) ||
      (/^[A-Z][A-Z0-9\s|™()-]{6,}$/.test(cur) && cur.length < 80);

    const endsSentence = /[.?!]$/.test(cur);
    const longish = cur.length > 90;
    const nextLooksNewBlock =
      /^(?:\d+\.\s+|SECTION\s+\d+\s*:|\*|\-|•)/i.test(next);

    if (isHeading) out.push('');
    else if (endsSentence && longish && !nextLooksNewBlock) out.push('');
  }

  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Extract text content from a PDF File object with structure preservation.
 * This replaces the naive "join with spaces" approach that destroys formatting.
 */
export async function extractTextFromPdf(file: File): Promise<string> {
  if (!window.pdfjsLib) {
    console.error('pdfjsLib is not loaded. Please ensure pdfjs-dist is available.');
    throw new Error('PDF.js library is not available to extract text from PDF.');
  }

  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${window.pdfjsLib.version}/pdf.worker.min.js`;

  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = window.pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;

  let fullText = '';

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);

    // Use normalizeWhitespace=false to keep tokens accurate, we do our own.
    const textContent = await page.getTextContent({ normalizeWhitespace: false, disableCombineTextItems: false });

    const pageText = reconstructPageText(textContent.items as TextItem[]);
    if (pageText) {
      fullText += pageText + '\n\n';
    }
  }

  // Final cleanup: remove stray multiple spaces and excessive blank lines
  fullText = fullText
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim();

  return fullText;
}